/**
 * @fileoverview Person Profile Component with Error Handling
 * @active MAINTAINED - Used in person research pages
 *
 * This component renders a person's profile with integrated error handling using
 * the reusable ApiErrorAlert component. It provides graceful fallbacks for API failures
 */

import { Box, Flex, Text } from "@chakra-ui/react";

import { ProfileChevron } from "@/components/companies/companiesList/newCompaniesList/companiesCards/companyCardItem/AcceleratorDetailPage";
import { RoslindaleText } from "@/components/ui/text/RoslindaleText";
import { h_400_14_100_64 } from "@/styles/fontStyles";
import { imgSelector } from "@/utils/imgSelector";
import { PersonData } from "@/types/personAggregated";
import { Person } from "@/types/person";

import DefaultImage from "../ui/DefaultImage";

/**
 * Extended PersonData interface to include headline bio field
 * This allows strong typing while maintaining compatibility
 */
interface PersonWithBio extends PersonData {
  personHeadlineBio?: string;
}

/**
 * Profile_id component props
 */
interface Profile_idProps {
  onePerson: PersonWithBio;
}

/**
 * Profile component for displaying a person's header information
 * Displays profile image, name, and bio
 *
 * @param {Profile_idProps} props - Component props
 * @returns {JSX.Element} The profile component
 */
function Profile_id({ onePerson }: Profile_idProps): JSX.Element {
  // Log the person data to help debug name display issues
  console.log('Profile_id received person data:', {
    id: onePerson?.id,
    fullName: onePerson?.fullName,
    firstName: onePerson?.firstName,
    lastName: onePerson?.lastName,
    nameFull: onePerson?.nameFull,
    nameFirst: onePerson?.nameFirst,
    nameLast: onePerson?.nameLast,
  });

  // Prioritize fullName if available, otherwise combine firstName and lastName
  // Also check for nameFull, nameFirst, nameLast as fallbacks
  const displayName = onePerson?.fullName || 
    onePerson?.nameFull ||
    [
      onePerson?.firstName || onePerson?.nameFirst, 
      onePerson?.lastName || onePerson?.nameLast
    ]
      ?.filter(Boolean)
      ?.join(" ");
  
  console.log('Profile_id displayName:', displayName);

  // Safely access the headline bio with type checking
  const headlineBio = onePerson?.personHeadlineBio;
  const hasBio =
    typeof headlineBio === "string" && headlineBio.trim().length > 0;

  return (
    <>
      <Flex
        alignItems={{ base: "center", md: "flex-start" }}
        flexDirection={{ base: "column" }}
        gap={"18px"}
      >
        <Flex
          flexDir={{ base: "column", md: "row" }}
          gap={"20px"}
          align={"center"}
        >
          <DefaultImage
            src={imgSelector({ data: onePerson, imgType: "person" })}
            type={"person"}
            options={{
              alt: "people",
              boxSize: "68px",
              objectFit: "cover",
              objectPosition: "top",
              borderRadius: "6px",
            }}
          />
          <RoslindaleText
            headingType={"h1"}
            customStyle={{ fontSize: { base: "28px" } }}
          >
            {displayName || "Profile"}
          </RoslindaleText>
        </Flex>
        {hasBio && (
          <Text
            color={"grey"}
            {...h_400_14_100_64}
            textAlign={{ base: "center", md: "left" }}
          >
            {headlineBio}
          </Text>
        )}
      </Flex>
      {/* ProfileChevron expects specific data structures for profile and item props */}
      <Box>
        <ProfileChevron 
          profile={onePerson} 
          item={{
            name: "Person",
            value: displayName || "Profile"
          }} 
        />
      </Box>
    </>
  );
}

export default Profile_id;
